import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Check, Minus, Plus, Save, Send } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  HIRE_KIND_LABELS,
  HIRE_KINDS,
  JEV_BUDGET_REQUEST_PRESETS,
  JOB_STATUS_LABELS,
  jevBudgetAmountProblem,
  REQUISITION_PRIORITIES,
  REQUISITION_PRIORITY_LABELS,
  startRangeProblem,
  budgetRangeProblem,
  EMPLOYMENT_TYPES,
  WORK_MODES,
  type EmploymentType,
  type JobDetail,
  type RequestEditMarks,
  type RequisitionDetail,
  type WorkMode,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { JevBudgetPicker } from '../../../components/JevBudgetPicker';
import { MoneyInput } from '../../../components/MoneyInput';
import { SaveBar } from '../../../components/SaveBar';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { Tabs, type TabItem } from '../../../components/Tabs';
import { ForbiddenState, NotFoundState } from '../../../components/system-states/SystemState';
import { ApiError, apiGet, errorMessage } from '../../../lib/api-client';
import { useCurrencyOptions, withCurrent } from '../../../lib/app-settings';
import { DEFAULT_TIME_ZONE } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { useCanEdit, useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useTeamToReplace } from '../../admin/api/staff-api';
import { useColleagues } from '../../admin/api/users-api';
import { jobKey, useJobs } from '../../jobs/api/jobs-api';
import { useDepartments, usePositions, useVentures } from '../../organisation/api/organisation-api';
import { useActionTimer } from '../api/hm-api';
import {
  useCreateRequisition,
  useRequisition,
  useRequisitionAction,
  useUpdateRequisition,
} from '../api/requisitions-api';
import { routeSentence, WhatHappensNext } from '../components/WhatHappensNext';
import { EditedMark } from '../components/EditedMark';
import { CompletionBar, RequestCompletionList } from '../components/RequestCompletion';
import { IdealCandidatePanel, JobDetailsPanel, OnboardingPanel, RolePanel } from '../components/RequestJobSections';
import { firstTabOf, PART_TABS, REQUEST_TAB_DESCRIPTIONS, REQUEST_TAB_LABELS, REQUEST_TABS, requestTabStates, type RequestTab } from '../request-tabs';
import { applyErrors, blankFor, completionOf, missingFor, prefillFromJob, requestFrom, valuesFrom, type FormValues } from '../requisition-form-values';
import { headingOf, wasSentBefore } from '../request-heading';
import styles from './RequestHirePage.module.css';

/**
 * Request a hire — the one form everyone uses (workflow review 2026-10-29, section A; the
 * approved mockup `Docs/Mockups/hire-requests-approval`). The left menu's button and the
 * quick-add button both open it; the old long REQ-01 form is gone.
 *
 * Which position: an existing one (filled or not — a seat is often replaced before it is
 * empty), then replacement or additional, and for a replacement who, from the requester's
 * own team (the staff list, ADR 0074); or a new one. Grade is gone. The start date and the
 * budget are ranges. The right side says what happens next, as the server will route it
 * (bug B8, §L41): HR checks, then the department's approval chain or Leadership, and the
 * draft job is made (ADR 0073).
 *
 * ADR 0075 (section B): the requester also writes the role, the brief, the ideal candidate
 * and the job's Basics (all but the recruiter), with a completion bar beside the form. HR
 * opens the same form at the HR check to change anything; each change is marked "Edited by HR".
 *
 * Owner, 2026-11-01 ("Request a hire as tabs"): the form is one card with five tabs, like the job's Basics. A tab shows a
 * red count of what it still needs to send and turns green once that is filled; each tab ends with a Next button.
 *
 * ADR 0083 (owner 2026-11-01 Q4–Q6): the Ideal candidate tab is the profile template, the 30 and 90-day plans are optional, and
 * "This role manages people" on the Position tab adds the Leadership add-on's parts to all three. Evening review 2026-11-01
 * (items 1 and 2): About the job follows The request, then the two plans as one "Onboarding plan" tab.
 */

const DEFAULT_CURRENCY = 'BDT';
const START_CHOICES = [1, 2, 3] as const;
const MAX_HIRES = 50;
const NO_MARKS: RequestEditMarks = {};
const DEFAULT_WORK_MODE: WorkMode = 'onsite';
const DEFAULT_EMPLOYMENT_TYPE: EmploymentType = 'full_time';
/** A job in the Position list: its value carries this prefix so it never collides with a seat's id. */
const JOB_PREFIX = 'job:';
const PAST_JOBS = 100;

function workModeOf(value: string | undefined): WorkMode | null {
  return WORK_MODES.find((mode) => mode === value) ?? null;
}

function employmentTypeOf(value: string | undefined): EmploymentType | null {
  return EMPLOYMENT_TYPES.find((type) => type === value) ?? null;
}

export function RequestHirePage(): ReactNode {
  const params = useParams({ strict: false });
  const editingId = typeof params.requisitionId === 'string' ? params.requisitionId : null;
  const existing = useRequisition(editingId);

  if (!useCanEdit('requisition')) {
    return <ForbiddenState resource="requesting hires" contact="Your admin" />;
  }
  if (editingId && existing.isError) return <NotFoundState />;
  if (editingId && (existing.isPending || !existing.data)) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={8} />
      </div>
    );
  }
  return <RequestHireForm key={editingId ?? 'new'} existing={existing.data ?? null} />;
}

function RequestHireForm({ existing }: { readonly existing: RequisitionDetail | null }): ReactNode {
  const currencyOptions = useCurrencyOptions();
  const navigate = useNavigate();
  const showToast = useToasts((state) => state.show);
  const ventures = useVentures();
  const departments = useDepartments();
  const positions = usePositions({ page: 1, pageSize: 200, status: 'vacant,planned,becoming_vacant,filled', requestable: true });
  // Evening review item 4: the jobs already made are offered under Existing position, newest first, one per title and department.
  const pastJobs = useJobs({ page: 1, pageSize: PAST_JOBS, scope: 'all' });
  const queryClient = useQueryClient();
  const [filledFrom, setFilledFrom] = useState<{ readonly reference: string; readonly title: string; readonly isNewSeat: boolean } | null>(null);
  const create = useCreateRequisition();
  const update = useUpdateRequisition();
  const act = useRequisitionAction();
  const timer = useActionTimer();
  const now = useNow();
  const session = useCurrentSession();
  const colleagues = useColleagues();
  const people = (colleagues.data ?? []).map((person) => ({ id: person.id, fullName: person.fullName }));

  const teamPositions = (positions.data?.rows ?? []).filter((p) => p.archivedAt === null && p.frozenAt === null);
  const liveVentures = (ventures.data ?? []).filter((v) => v.archivedAt === null);
  const liveDepartments = (departments.data ?? []).filter((d) => d.archivedAt === null);

  const form = useForm<FormValues>({
    defaultValues: existing
      ? valuesFrom(existing)
      : {
          ...blankFor(session?.id ?? null),
          ventureId: liveVentures.length === 1 ? (liveVentures[0]?.id ?? null) : null,
          budgetCurrency: DEFAULT_CURRENCY,
        },
    mode: 'onBlur',
  });
  // QA D79: only a request that was sent before (and came back) is "fixed" and "sent again"; a draft never sent is still new.
  const wasSent = wasSentBefore(existing);
  const [saved, setSaved] = useState<{ id: string; version: number } | null>(
    existing ? { id: existing.id, version: existing.version } : null,
  );
  // A draft, or a request at the HR check, opens on the first tab still missing something.
  const [tab, setTab] = useState<RequestTab>(() => (existing ? firstTabOf(Object.keys(missingFor(form.getValues()))) : null) ?? 'position');
  const tabsCard = useRef<HTMLDivElement>(null);
  const [mode, positionId, slots, hireKind, ventureId, budgetCurrency] = useWatch({
    control: form.control,
    name: ['mode', 'positionId', 'slotsRequested', 'hireKind', 'ventureId', 'budgetCurrency'],
  });
  // Any change re-renders the form, so the completion bar and the send check read what is typed now.
  useWatch({ control: form.control });
  const current = form.getValues();
  const isReplacement = mode === 'existing' && hireKind === 'replacement';
  // QA D50: on a request someone else raised, the people replaceable are the requester's team, and the saved choice stays.
  const othersRequest = existing !== null && existing.requestedByUserId !== null && existing.requestedByUserId !== session?.id;
  const requesterName = othersRequest ? (existing?.requestedByName ?? 'the requester') : null;
  const team = useTeamToReplace(othersRequest ? (existing?.id ?? null) : null, isReplacement);
  const teamOptions = (team.data ?? []).map((member) => ({
    value: member.id,
    label: member.fullName,
    description: member.jobTitle ?? undefined,
    searchText: `${member.fullName} ${member.email}`,
  }));
  const savedChoice = existing?.replacesStaffId && existing.replacesName && !teamOptions.some((option) => option.value === existing.replacesStaffId)
    ? [{ value: existing.replacesStaffId, label: existing.replacesName, description: undefined, searchText: existing.replacesName }]
    : [];
  const replaceOptions = [...savedChoice, ...teamOptions];

  // ADR 0075 (B2): HR opens the same form at the HR check; anyone else sees a sent request is not theirs to change.
  const isCheck = existing !== null && existing.status !== 'draft' && existing.actions.includes('edit');
  const marks = existing?.editMarks ?? NO_MARKS;
  if (existing && existing.status !== 'draft' && !isCheck) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{existing.reference} can't be changed here</h1>
        <p className={styles.lead}>Only a draft can be edited. It's on My openings.</p>
        <Button variant="outline" onClick={() => void navigate({ to: '/my/openings', search: { open: existing.id } })}>
          Open it
        </Button>
      </div>
    );
  }

  const hasPositions = teamPositions.length > 0;
  // Bug B8: the department decides the route (a chain is chosen by department), so the card asks for that one's.
  const routeDepartmentId =
    mode === 'existing'
      ? (teamPositions.find((p) => p.id === positionId)?.departmentId ?? (existing && positionId === existing.positionId ? existing.departmentId : null))
      : (current.departmentId ?? null);
  const jobChoices = (pastJobs.data?.rows ?? []).filter(
    (job, index, rows) => rows.findIndex((other) => other.title === job.title && other.departmentId === job.departmentId) === index,
  );
  const hasChoices = hasPositions || jobChoices.length > 0;
  const chosen = teamPositions.find((p) => p.id === positionId) ?? null;
  const venture = ventureId ?? existing?.ventureId ?? null;
  const ventureDepartments = liveDepartments.filter((d) => venture === null || d.ventureId === venture);
  const isBusy = create.isPending || update.isPending || act.isPending;
  const errors = form.formState.errors;
  const missing = missingFor(current);
  const missingCount = Object.keys(missing).length;
  const completion = completionOf(current);
  const tabStates = requestTabStates(completion, [...Object.keys(missing), ...Object.keys(errors)]);
  const nextTab = REQUEST_TABS[REQUEST_TABS.indexOf(tab) + 1];
  const fallback = {
    workMode: workModeOf(chosen?.workMode) ?? DEFAULT_WORK_MODE,
    employmentType: employmentTypeOf(chosen?.employmentType) ?? DEFAULT_EMPLOYMENT_TYPE,
    location: chosen?.location ?? null,
    title: chosen?.title ?? current.title,
  };

  async function save(andSubmit: boolean): Promise<void> {
    const values = form.getValues();
    // A Jev budget typed below $1 or above $1,000 is called out under the field and never sent (nor saved) as it stands.
    const budgetProblem = jevBudgetAmountProblem(values.jevBudgetMicroUsd);
    if (budgetProblem) {
      form.setError('jevBudgetMicroUsd', { message: budgetProblem });
      openTab('request');
      return;
    }
    const startProblem = startRangeProblem(values.targetStartDate, values.targetStartLatest);
    if (startProblem) {
      form.setError('targetStartLatest', { message: startProblem });
      openTab('request');
      return;
    }
    // QA D48: a budget's top below its bottom is called out beside the budget on Save draft too, not only on Send.
    const asked = requestFrom(values);
    const budgetOrder = budgetRangeProblem(asked.budgetAmountMinor, asked.budgetMaxMinor);
    if (budgetOrder) {
      form.setError('budget', { message: budgetOrder });
      openTab('request');
      return;
    }
    // At the HR check the position and the hire stay as requested (the server refuses a change), so they are not sent.
    const { positionId: _position, departmentId: _department, title: _title, hireKind: _kind, replacesStaffId: _replaces, ...content } = requestFrom(values);
    const body = isCheck ? content : requestFrom(values);
    if (andSubmit || isCheck) {
      const stillMissing = missingFor(values);
      if (Object.keys(stillMissing).length > 0) {
        applyErrors(stillMissing, form.setError);
        openTab(firstTabOf(Object.keys(stillMissing)) ?? tab);
        return;
      }
    }

    try {
      if (isCheck && saved) {
        await update.mutateAsync({ id: saved.id, version: saved.version, ...body });
        showToast({ tone: 'success', message: 'Changes saved. Each one shows as Edited by HR.' });
        await navigate({ to: '/requisitions/$requisitionId', params: { requisitionId: saved.id } });
        return;
      }
      const next = saved
        ? await update.mutateAsync({ id: saved.id, version: saved.version, ...body }).then((d) => ({ id: d.id, version: d.version }))
        : await create.mutateAsync(body).then((created) => ({ id: created.id, version: 1 }));
      setSaved(next);
      if (!andSubmit) {
        showToast({ tone: 'success', message: 'Draft saved. It waits on My actions.' });
        await navigate({ to: '/my/actions' });
        return;
      }
      const submitted = await act.mutateAsync({ id: next.id, action: 'submit', version: next.version });
      timer.finish(wasSent ? 'resubmit' : 'request_hire', next.id);
      // Bug B8: the route the server actually gave it, never a promise written here.
      showToast({ tone: 'success', message: `${submitted.reference} sent for approval`, detail: routeSentence(submitted.approvalTrack) });
      await navigate({ to: '/my/openings', search: { open: next.id } });
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        applyErrors(cause.fields, form.setError);
        openTab(firstTabOf(Object.keys(cause.fields)) ?? tab);
        return;
      }
      showToast({ tone: 'danger', message: 'That was not sent', detail: errorMessage(cause) });
    }
  }

  /** Opens a tab and, when the tab row has scrolled out of view, brings it back. */
  function openTab(next: RequestTab): void {
    setTab(next);
    const card = tabsCard.current;
    if (card && card.getBoundingClientRect().top < 0) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const tabItems: readonly TabItem<RequestTab>[] = REQUEST_TABS.map((key) => {
    const state = tabStates[key];
    const label = REQUEST_TAB_LABELS[key];
    if (state.toFix > 0) return { value: key, label, after: <span className={styles.toFix}>{state.toFix}</span>, afterLabel: `${state.toFix} needed to send` };
    if (state.isDone) return { value: key, label, isDone: true, after: <Check size={16} />, afterLabel: 'filled in' };
    return { value: key, label, after: <span className={styles.optionalWord}>optional</span> };
  });
  const tabMarks: Partial<Record<RequestTab, ReactNode>> = {
    position: (
      <>
        <EditedMark mark={marks.hire} />
        <EditedMark mark={marks.managesPeople} />
      </>
    ),
    ideal: <EditedMark mark={marks.idealCandidate} />,
    onboarding: (
      <>
        <EditedMark mark={marks.plan_30} />
        <EditedMark mark={marks.plan_90} />
      </>
    ),
  };

  function setStartIn(months: number): void {
    form.setValue('targetStartDate', isoDateInMonths(now, months), { shouldDirty: true, shouldValidate: true });
    form.clearErrors('targetStartDate');
    checkStartRange();
  }

  /** Evening review item 3: a latest start before the earliest is called out the moment both are picked. */
  function checkStartRange(): void {
    const problem = startRangeProblem(form.getValues('targetStartDate'), form.getValues('targetStartLatest'));
    if (problem) form.setError('targetStartLatest', { message: problem });
    else form.clearErrors('targetStartLatest');
  }

  function stepHires(by: number): void {
    const current = Number(slots) || 1;
    form.setValue('slotsRequested', String(Math.min(MAX_HIRES, Math.max(1, current + by))), { shouldDirty: true });
  }

  /** Lazy default: the seat's work mode, type and place, unless the requester already chose. */
  function prefillFrom(positionId: string | null): void {
    const position = teamPositions.find((p) => p.id === positionId);
    if (!position) return;
    if (form.getValues('workMode') === null) form.setValue('workMode', workModeOf(position.workMode));
    if (form.getValues('employmentType') === null) form.setValue('employmentType', employmentTypeOf(position.employmentType));
  }

  /** Item 4: picking a job made before fills the request in from it (only what's still empty). */
  async function pickJob(jobId: string): Promise<void> {
    try {
      const job = await queryClient.fetchQuery({ queryKey: jobKey(jobId), queryFn: () => apiGet<JobDetail>(`/jobs/${jobId}`) });
      const next = prefillFromJob(form.getValues(), job, new Set(teamPositions.map((p) => p.id)));
      form.reset(next, { keepDirty: false, keepErrors: false, keepDefaultValues: true });
      setFilledFrom({ reference: job.reference, title: job.title, isNewSeat: next.mode === 'new' });
      form.clearErrors(['positionId', 'departmentId', 'title']);
    } catch (cause) {
      showToast({ tone: 'danger', message: "That job couldn't be read", detail: errorMessage(cause) });
    }
  }

  function pickMode(next: FormValues['mode']): void {
    setFilledFrom(null);
    form.setValue('mode', next, { shouldDirty: true });
    if (next === 'new') {
      form.setValue('positionId', null);
      form.setValue('hireKind', null);
      form.setValue('replacesStaffId', null);
    }
    form.clearErrors(['positionId', 'departmentId', 'title', 'hireKind', 'replacesStaffId']);
  }

  return (
    <form
      className={styles.page}
      onSubmit={(event) => {
        event.preventDefault();
        void save(true);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void save(true);
        }
      }}
    >
      <header className={styles.head}>
        <h1 className={styles.title}>{headingOf(existing, isCheck)}</h1>
        {isCheck && (
          <p className={styles.lead}>
            You are checking what {existing?.requestedByName ?? 'the requester'} wrote. Change anything that is unclear or unrealistic; each change
            shows as “Edited by HR”, with what they first wrote.
          </p>
        )}
        {!isCheck && existing?.statusReason && (
          <p className={styles.returned} role="status">
            Sent back: “{existing.statusReason}”
          </p>
        )}
        <div className={styles.mobileProgress}>
          <CompletionBar percent={completion.percent} label="How complete" />
        </div>
      </header>

      <div className={styles.layout}>
        <div className={styles.main}>
          <div ref={tabsCard} className={styles.anchor}>
          <Card title="Hire details" meta="A tab turns green once it's filled in" isFlush>
          <Tabs label="Hire details" tabs={tabItems} value={tab} onChange={openTab}>
            <div className={styles.tabNote}>
              <p>{REQUEST_TAB_DESCRIPTIONS[tab]}</p>
              {tabMarks[tab]}
            </div>
            <div className={styles.panel}>
            {tab === 'position' && (isCheck && existing ? (
              <div className={styles.facts}>
                <span>
                  <b>{existing.title}</b>
                </span>
                <span>{`${existing.departmentName} · ${existing.ventureName}`}</span>
                {existing.positionCode && <span>{existing.positionCode}</span>}
                {existing.hireKind && <span>{existing.replacesName ? `${HIRE_KIND_LABELS[existing.hireKind]}: ${existing.replacesName}` : HIRE_KIND_LABELS[existing.hireKind]}</span>}
                <span className={styles.small}>The position stays as requested at the HR check. To change it, send the request back.</span>
              </div>
            ) : (
            <div className={styles.fields}>
              <Controller
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <div className={styles.choices} role="radiogroup" aria-label="Which position">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={field.value === 'existing'}
                      className={styles.choice}
                      onClick={() => pickMode('existing')}
                    >
                      <span className={styles.choiceTitle}>Existing position</span>
                      <span className={styles.choiceMeta}>Someone already does this job, or the seat exists in the team</span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={field.value === 'new'}
                      className={styles.choice}
                      onClick={() => pickMode('new')}
                    >
                      <span className={styles.choiceTitle}>New position</span>
                      <span className={styles.choiceMeta}>A job the team has never had</span>
                    </button>
                  </div>
                )}
              />

              {filledFrom && (
                <p className={styles.note} role="status">
                  Filled in from {filledFrom.reference} · {filledFrom.title}. Change anything that&rsquo;s different this time.
                  {filledFrom.isNewSeat && ' That job has no free seat on record, so this goes as a new position with the same title.'}
                </p>
              )}

              {mode === 'existing' && (
                <>
                  <Field label="Position" isRequired error={errors.positionId?.message}>
                    {({ inputId, isInvalid }) =>
                      positions.isPending ? (
                        <SkeletonLines count={1} />
                      ) : hasChoices ? (
                        <Controller
                          control={form.control}
                          name="positionId"
                          render={({ field }) => (
                            <Select
                              id={inputId}
                              label="Position"
                              isInvalid={isInvalid}
                              placeholder="Choose a position"
                              value={field.value}
                              options={[
                                ...teamPositions.map((p) => ({
                                  value: p.id,
                                  label: `${p.title} · ${p.departmentName}`,
                                  description: `Position ${p.code} · ${p.ventureName}`,
                                })),
                                ...jobChoices.map((job) => ({
                                  value: `${JOB_PREFIX}${job.id}`,
                                  label: `${job.title} · ${job.departmentName}`,
                                  description: `Job ${job.reference} · ${JOB_STATUS_LABELS[job.status]} · fills this request in from it`,
                                  searchText: `${job.title} ${job.departmentName} ${job.reference}`,
                                })),
                              ]}
                              onChange={(value) => {
                                if (value.startsWith(JOB_PREFIX)) {
                                  void pickJob(value.slice(JOB_PREFIX.length));
                                  return;
                                }
                                setFilledFrom(null);
                                field.onChange(value);
                                form.clearErrors('positionId');
                                prefillFrom(value);
                              }}
                            />
                          )}
                        />
                      ) : (
                        <p className={styles.note}>
                          Your team has no positions or jobs on record yet. Choose New position, or ask HR to add the seat.
                        </p>
                      )
                    }
                  </Field>

                  {chosen && (
                    <div className={styles.facts}>
                      <span>
                        Seats <b>{chosen.totalSlots}</b>
                      </span>
                      <span>
                        Filled <b>{chosen.totalSlots - chosen.openSlots}</b>
                      </span>
                      <span>
                        Empty <b>{chosen.openSlots}</b>
                      </span>
                    </div>
                  )}

                  {hasPositions && (
                  <Field label="Is this a replacement?" isRequired error={errors.hireKind?.message}>
                    {() => (
                      <Controller
                        control={form.control}
                        name="hireKind"
                        render={({ field }) => (
                          <div className={styles.segmented} role="radiogroup" aria-label="Is this a replacement?">
                            {HIRE_KINDS.map((kind) => (
                              <button
                                key={kind}
                                type="button"
                                role="radio"
                                aria-checked={field.value === kind}
                                className={styles.segment}
                                onClick={() => {
                                  field.onChange(kind);
                                  form.clearErrors('hireKind');
                                  if (kind === 'additional') form.setValue('replacesStaffId', null);
                                }}
                              >
                                {HIRE_KIND_LABELS[kind]}
                              </button>
                            ))}
                          </div>
                        )}
                      />
                    )}
                  </Field>
                  )}

                  {isReplacement && hasPositions && (
                    <Field
                      label="Who is being replaced?"
                      isRequired
                      hint={`Only people who report to ${requesterName ?? 'you'}. The list comes from HR's staff list.`}
                      error={errors.replacesStaffId?.message}
                    >
                      {({ inputId, isInvalid }) =>
                        team.isPending ? (
                          <SkeletonLines count={1} />
                        ) : replaceOptions.length === 0 ? (
                          <p className={styles.note}>
                            {requesterName
                              ? `Nobody reports to ${requesterName} on the staff list.`
                              : "Nobody reports to you on the staff list. A replacement is raised by the leaving person's line manager. If that's you, ask HR to update the staff list."}
                          </p>
                        ) : (
                          <Controller
                            control={form.control}
                            name="replacesStaffId"
                            render={({ field }) => (
                              <Select
                                id={inputId}
                                label="Who is being replaced?"
                                isInvalid={isInvalid}
                                placeholder={requesterName ? `Choose someone from ${requesterName}'s team` : 'Choose someone from your team'}
                                value={field.value}
                                options={replaceOptions}
                                onChange={(value) => {
                                  field.onChange(value);
                                  form.clearErrors('replacesStaffId');
                                }}
                              />
                            )}
                          />
                        )
                      }
                    </Field>
                  )}
                </>
              )}

              {mode === 'new' && (
                <>
                  <div className={styles.row}>
                    <Field label="Venture" isRequired>
                      {({ inputId }) => (
                        <Controller
                          control={form.control}
                          name="ventureId"
                          render={({ field }) => (
                            <Select
                              id={inputId}
                              label="Venture"
                              placeholder="Choose a venture"
                              value={field.value}
                              options={liveVentures.map((v) => ({ value: v.id, label: v.name }))}
                              onChange={(value) => {
                                field.onChange(value);
                                form.setValue('departmentId', null);
                              }}
                            />
                          )}
                        />
                      )}
                    </Field>
                    <Field label="Department" isRequired error={errors.departmentId?.message}>
                      {({ inputId, isInvalid }) => (
                        <Controller
                          control={form.control}
                          name="departmentId"
                          render={({ field }) => (
                            <Select
                              id={inputId}
                              label="Department"
                              isInvalid={isInvalid}
                              placeholder="Choose a department"
                              value={field.value}
                              options={ventureDepartments.map((d) => ({ value: d.id, label: d.name }))}
                              onChange={(value) => {
                                field.onChange(value);
                                form.clearErrors('departmentId');
                              }}
                            />
                          )}
                        />
                      )}
                    </Field>
                  </div>
                  <Field label="Job title" isRequired error={errors.title?.message} action={<EditedMark mark={marks.title} />}>
                    {({ inputId, isInvalid }) => (
                      <Input id={inputId} isInvalid={isInvalid} placeholder="For example: Client success executive" {...form.register('title')} />
                    )}
                  </Field>
                </>
              )}
            </div>
            ))}

            {tab === 'position' && (
              <Field label="This role manages people" hint="Adds the Leadership add-on: its parts join the ideal candidate and the 30 and 90-day plans.">
                {() => (
                  <Controller
                    control={form.control}
                    name="managesPeople"
                    render={({ field }) => <Switch checked={field.value} label={field.value ? 'Yes, they will manage a team' : 'No'} onChange={field.onChange} />}
                  />
                )}
              </Field>
            )}

            {tab === 'request' && (
            <div className={styles.fields}>
              <div className={styles.row}>
                <Field label="How many people" error={errors.slotsRequested?.message} action={<EditedMark mark={marks.slotsRequested} />}>
                  {({ inputId }) => (
                    <div className={styles.stepper}>
                      <Button variant="outline" icon={Minus} aria-label="One fewer" disabled={Number(slots) <= 1} onClick={() => stepHires(-1)} />
                      <Input id={inputId} isNumeric inputMode="numeric" aria-label="How many people" {...form.register('slotsRequested')} />
                      <Button variant="outline" icon={Plus} aria-label="One more" disabled={Number(slots) >= MAX_HIRES} onClick={() => stepHires(1)} />
                    </div>
                  )}
                </Field>

                <Field label="How urgent" action={<EditedMark mark={marks.priority} />}>
                  {() => (
                    <Controller
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <div className={styles.segmented} role="radiogroup" aria-label="How urgent">
                          {REQUISITION_PRIORITIES.map((priority) => (
                            <button
                              key={priority}
                              type="button"
                              role="radio"
                              aria-checked={field.value === priority}
                              className={styles.segment}
                              data-tone={priority}
                              onClick={() => field.onChange(priority)}
                            >
                              {REQUISITION_PRIORITY_LABELS[priority]}
                            </button>
                          ))}
                        </div>
                      )}
                    />
                  )}
                </Field>
              </div>

              <Field
                label="When should they start?"
                isRequired
                error={errors.targetStartDate?.message ?? errors.targetStartLatest?.message}
                action={<EditedMark mark={marks.start} />}
              >
                {({ inputId, isInvalid }) => (
                  <div className={styles.startBy}>
                    <div className={styles.range}>
                      <Controller
                        control={form.control}
                        name="targetStartDate"
                        render={({ field }) => (
                          <DatePicker
                            id={inputId}
                            label="Earliest start date"
                            placeholder="Earliest start"
                            max={current.targetStartLatest || undefined}
                            isInvalid={isInvalid && Boolean(errors.targetStartDate)}
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              field.onBlur();
                              checkStartRange();
                            }}
                          />
                        )}
                      />
                      <span className={styles.to}>to</span>
                      <Controller
                        control={form.control}
                        name="targetStartLatest"
                        render={({ field }) => (
                          <DatePicker
                            label="Latest start date"
                            placeholder="Latest start"
                            min={current.targetStartDate || undefined}
                            isInvalid={Boolean(errors.targetStartLatest)}
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              field.onBlur();
                              checkStartRange();
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className={styles.quick}>
                      {START_CHOICES.map((months) => (
                        <button key={months} type="button" className={styles.pill} onClick={() => setStartIn(months)}>
                          In {months} {months === 1 ? 'month' : 'months'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </Field>

              <Field
                label="Budget"
                isOptional
                hint="Per person, per month, gross. It goes into the job as its salary budget."
                error={errors.budget?.message ?? errors.budgetMax?.message ?? errors.budgetCurrency?.message}
                action={<EditedMark mark={marks.budget} />}
              >
                {({ inputId, isInvalid }) => (
                  <div className={styles.range}>
                    <Controller
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <MoneyInput id={inputId} label="Budget from" currency={budgetCurrency ?? DEFAULT_CURRENCY} isInvalid={isInvalid} value={field.value} onChange={field.onChange} />
                      )}
                    />
                    <span className={styles.to}>to</span>
                    <Controller
                      control={form.control}
                      name="budgetMax"
                      render={({ field }) => (
                        <MoneyInput label="Budget up to" currency={budgetCurrency ?? DEFAULT_CURRENCY} isInvalid={Boolean(errors.budgetMax)} value={field.value} onChange={field.onChange} />
                      )}
                    />
                    <div className={styles.currency}>
                      <Controller
                        control={form.control}
                        name="budgetCurrency"
                        render={({ field }) => (
                          <Select label="Currency" value={field.value} options={withCurrent(currencyOptions, field.value)} onChange={field.onChange} />
                        )}
                      />
                    </div>
                  </div>
                )}
              </Field>

              {/* Evening review 2026-11-01 item 5 (DECISIONS-LOG 2026-11-02 Q2 a): requesters didn't understand the Jev budget, so only HR
                  sees it, at the HR check. A request sent without one gets the default; the HR-budget rule comes later. */}
              {isCheck && (
              <Field
                label="Jev budget for this job"
                isOptional
                hint="What Jev may cost on this job: building the ideal candidate and reading CVs. The approver can change it."
                error={errors.jevBudgetMicroUsd?.message}
                action={<EditedMark mark={marks.jevBudget} />}
              >
                {({ inputId }) => (
                  <Controller
                    control={form.control}
                    name="jevBudgetMicroUsd"
                    render={({ field }) => (
                      <JevBudgetPicker
                        id={inputId}
                        label="Jev budget for this job"
                        value={field.value}
                        presets={JEV_BUDGET_REQUEST_PRESETS}
                        onChange={(next) => {
                          field.onChange(next);
                          form.clearErrors('jevBudgetMicroUsd');
                        }}
                      />
                    )}
                  />
                )}
              </Field>
              )}
            </div>
            )}

            {tab === 'role' && <RolePanel form={form} marks={marks} />}
            {tab === 'ideal' && <IdealCandidatePanel form={form} />}
            {tab === 'job' && <JobDetailsPanel form={form} marks={marks} people={people} fallback={fallback} />}
            {tab === 'onboarding' && <OnboardingPanel form={form} />}

            {nextTab && (
              <div className={styles.next}>
                <Button variant="outline" onClick={() => openTab(nextTab)}>
                  Next: {REQUEST_TAB_LABELS[nextTab]}
                </Button>
              </div>
            )}
            </div>
          </Tabs>
          </Card>
          </div>

          {isCheck ? (
            <SaveBar status={missingCount === 0 ? 'Changes are marked Edited by HR' : `${missingCount} ${missingCount === 1 ? 'thing' : 'things'} left to fill in`}>
              <Button variant="ghost" onClick={() => void navigate({ to: '/requisitions/$requisitionId', params: { requisitionId: existing?.id ?? '' } })}>
                Cancel
              </Button>
              <Button type="submit" variant="solid" icon={Save} isPending={update.isPending}>
                Save changes
              </Button>
            </SaveBar>
          ) : (
            <SaveBar status={missingCount === 0 ? `Ready to send · ${completion.percent}% complete` : `${missingCount} ${missingCount === 1 ? 'thing' : 'things'} left to fill in`}>
              <Button variant="ghost" isPending={isBusy && !act.isPending} onClick={() => void save(false)}>
                Save draft
              </Button>
              <Button type="submit" variant="solid" icon={Send} isPending={act.isPending}>
                {wasSent ? 'Send it again' : 'Send for approval'}
              </Button>
            </SaveBar>
          )}
        </div>

        <aside className={styles.aside}>
          <Card title="How complete">
            <RequestCompletionList completion={completion} onJump={(part) => openTab(PART_TABS[part])} />
          </Card>
          <WhatHappensNext check={isCheck ? existing : null} departmentId={routeDepartmentId} />
        </aside>
      </div>
    </form>
  );
}

/** A date `months` from `now`, as the calendar reads it in Dhaka (D14). */
function isoDateInMonths(now: number, months: number): string {
  const date = new Date(now);
  date.setMonth(date.getMonth() + months);
  return new Intl.DateTimeFormat('en-CA', { timeZone: DEFAULT_TIME_ZONE }).format(date);
}
